import { useState } from 'react';
import { SessionMetadata } from '../types';
import { Trash2, FileText, Clock, Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  session: SessionMetadata;
  isActive: boolean;
  onClick: () => void;
  onDelete: () => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays === 1) {
    return 'yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
}

export default function SessionCard({ session, isActive, onClick, onDelete }: Props) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(true);
    try {
      onDelete();
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  return (
    <div
      onClick={onClick}
      className={clsx(
        'group relative p-3 rounded-lg cursor-pointer transition-all duration-150',
        isActive
          ? 'bg-accent/10 border border-accent/30 shadow-sm'
          : 'hover:bg-gray-50 border border-transparent'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText
              size={14}
              className={clsx(
                'flex-shrink-0',
                isActive ? 'text-accent' : 'text-gray-400'
              )}
            />
            <h3
              className={clsx(
                'font-medium truncate text-sm',
                isActive ? 'text-accent' : 'text-gray-900'
              )}
            >
              {session.name}
            </h3>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
            <span className="flex items-center gap-0.5">
              <Clock size={10} />
              {formatRelativeTime(session.updatedAt)}
            </span>
            <span className="text-gray-300">|</span>
            <span>{session.sectionCount} {session.sectionCount === 1 ? 'section' : 'sections'}</span>
            <span className="text-gray-300">|</span>
            <span>{session.wordCount} words</span>
          </div>

          {/* Document type badge */}
          {session.documentType && session.documentType !== 'general' && (
            <span className="inline-block mt-1.5 px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
              {session.documentType}
            </span>
          )}

          {/* Preview text */}
          {session.preview && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{session.preview}</p>
          )}
        </div>

        {/* Delete button or confirmation */}
        {showDeleteConfirm ? (
          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {isDeleting ? <Loader2 size={12} className="animate-spin" /> : 'Delete'}
            </button>
            <button
              onClick={handleCancelDelete}
              className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={handleDeleteClick}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            title="Delete session"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
